import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, inject, NgModule, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, NgxsModule, ofActionErrored, ofActionSuccessful, select } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoginState, LoginStateLogin } from './login.state';
import { AnimateModule, HeaderComponent, NewsletterComponent, ValidateMessageComponent } from '@pro-connect/shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  GtmClickDirective,
  InputShowPasswordDirective,
  InputValidatorDirective,
  InvalidControlScrollDirective,
} from '../../../../directives';
import { BasicPage, WwwRouteSlug } from '../../../../models';
import { FormErrorHandlerService } from '../../../../services';

export type LoginFormType = {
  username: FormControl<string | null>;
  password: FormControl<string | null>;
};
import { Angulartics2GoogleTagManager } from 'angulartics2';

@NgModule({
  imports: [NgxsModule.forFeature([LoginState])],
})
class LoginStateLazyModule {}

@Component({
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InvalidControlScrollDirective,
    InputValidatorDirective,
    InputShowPasswordDirective,
    LoginStateLazyModule,
    NgOptimizedImage,
    NewsletterComponent,
    HeaderComponent,
    GtmClickDirective,
  ],
})
export class LoginComponent extends BasicPage implements OnInit {
  public override readonly loading = select(LoginState.loading);
  protected readonly WwwRouteSlug = WwwRouteSlug;
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _angulartics2GoogleTagManager = inject(Angulartics2GoogleTagManager);
  public readonly formGroup: FormGroup<LoginFormType> = this._formBuilder.group({
    username: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
    password: new FormControl<string | null>(null, [Validators.required, Validators.maxLength(255)]),
  });

  constructor(
    private readonly _actions$: Actions,
    private readonly _toastrService: ToastrService,
    private readonly _formErrorHandlerService: FormErrorHandlerService,
  ) {
    super();
  }

  get f() {
    return this.formGroup.controls;
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKeydownHandler(event: KeyboardEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.onLogin();
  }

  public ngOnInit(): void {
    this._setPasswordChangeSubscription();
    this._setActionsSubscriptions();
    this._pushLayer();
  }

  public onLogin(): void {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      return;
    }
    this._store
      .dispatch(new LoginStateLogin())
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((error) => {
          if (error === 'timeout') {
            this._toastrService.error('Błąd przekroczenia limitu czasu żądania', 'Błąd');
          }
          this._formErrorHandlerService.catchFormError(error, this.formGroup);
          if (error?.error?.code === 401) {
            this._toastrService.error(error.error.message);
          }
          return throwError(() => error);
        }),
      )
      .subscribe({});
  }

  private _setActionsSubscriptions(): void {
    this._actions$.pipe(takeUntilDestroyed(this._destroyRef), ofActionSuccessful(LoginStateLogin)).subscribe(() => {
      this._store.dispatch(new Navigate([`/${WwwRouteSlug.ACCOUNT}`]));
      this._pushLayerOnSuccessfullLogin();
    });
    this._actions$.pipe(takeUntilDestroyed(this._destroyRef), ofActionErrored(LoginStateLogin)).subscribe(() => {
      this._toastrService.error('Coś poszło nie tak.', 'Błąd');
    });
  }

  private _setPasswordChangeSubscription(): void {
    const _clearUserNameBackendError = () => {
      if (this.f.username?.errors?.['backend']) {
        this.f.username.setErrors(null);
      }
    };

    this.f.password.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      _clearUserNameBackendError();
    });
  }

  private _pushLayerOnSuccessfullLogin(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      event: 'uaevent',
      ecommerce: 'undefined',
      eventCategory: 'log in',
      eventAction: 'regular',
      eventLabel: 'my account',
      event_name: 'login',
      method: 'regular',
      referral: 'my account',
    });
  }

  private _pushLayer(): void {
    this._angulartics2GoogleTagManager.pushLayer({
      brand: 'LDB',
      language: 'pl',
      country: 'Pl',
      siteTypeLevel: this._isDevelopment ? 'test' : 'main',
      pageCategory: 'my account',
      geoIPLocation: 'Pl',
      uiLoggedStatus: 'not logged',
    });
  }
}
